"use client";

import { Core } from "@faire/design-tokens";
import {
  IAction,
  renderTextAction,
} from "@faire/web--source/common/buttonUtils";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import * as React from "react";
import styled, { DefaultTheme, useTheme } from "styled-components";

import { Color } from "../../slate/Color";
import { Close } from "../../slate/icons/foundation/Close";
import { getSpacing, Spacer } from "../../slate/spacing";

const Wrapper = styled(Row).attrs({ align: "center", justify: "center" })<{
  $color?: Color;
}>`
  background: ${(props) =>
    props.$color ?? props.theme.banners?.alert?.color ?? Core.surface.warning};
  padding: ${getSpacing(12, "2x")};
  width: 100%;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  display: flex;
  font-size: 20px;
`;

export interface IAlertBannerProps {
  color?: Color;
  message: string | React.ReactNode;
  textColor?: Color;
  // Link, button, or anchor link action
  alertAction?: IAction;
  onClose?: () => void;
  className?: string;
  centerAlignIcon?: boolean;
}

/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/retailer/component/alert-banner)
 */
export const AlertBanner: React.FC<IAlertBannerProps> = ({
  color,
  onClose,
  textColor,
  message,
  alertAction,
  className,
  centerAlignIcon,
}) => {
  const theme = useTheme();
  const alertActionComponent = renderTextAction(alertAction, {
    textColor: textColor ?? theme?.banners?.alert?.textColor,
  });
  return (
    <Wrapper $color={color} className={className}>
      {typeof message === "string" ? (
        <Row align={centerAlignIcon ? "center" : undefined}>
          {renderEventsIcon(theme)}
          <Typography
            color={textColor ?? theme?.banners?.alert?.textColor}
            align="center"
          >
            {message}
            {alertActionComponent ? <> {alertActionComponent}</> : null}
          </Typography>
        </Row>
      ) : (
        <Row align={centerAlignIcon ? "center" : undefined}>
          {renderEventsIcon(theme)}
          {message}
          {alertActionComponent ? (
            <>
              <Spacer size={4} />
              {alertActionComponent}
            </>
          ) : null}
        </Row>
      )}
      {onClose ? (
        <CloseButton onClick={onClose}>
          <Close fontSize="16px" />
        </CloseButton>
      ) : null}
    </Wrapper>
  );
};

const renderEventsIcon = (theme: DefaultTheme) => {
  if (!theme?.eventIcon) {
    return null;
  }

  const Component = theme.eventIcon.Component;

  return (
    <Component
      data-test-id="alert-banner-event-icon"
      width="20px"
      height="20px"
      margin="0 8px 0 0"
    />
  );
};
